import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "../Button";
import { IconButton } from "../IconButton";

export type NotificationBellItem = {
  id: string;
  title: string;
  body: string;
  timeLabel?: string;
  read: boolean;
};

export type NotificationBellPopoverProps = {
  ariaLabel: string;
  title: string;
  markAllReadLabel: string;
  emptyLabel: string;
  items: readonly NotificationBellItem[];
  unreadCount?: number;
  onMarkAllRead: () => void | Promise<void>;
  onItemClick: (item: NotificationBellItem) => void | Promise<void>;
  notice?: {
    title: string;
    body: string;
    actionLabel?: string;
    onAction?: () => void | Promise<void>;
    actionLoading?: boolean;
  };
  /** Optional trigger override for a dark or compact header. */
  triggerClassName?: string;
};

type PanelPosition = { top: number; left: number; width: number };

function BellIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

/**
 * Presentation-only notification bell and viewport-clamped popover.
 *
 * Data ownership, translation, routing and read persistence stay in the app.
 * Keeping those dependencies out of this component lets seller and restaurant
 * share one accessible presentation without coupling their notification
 * contracts or routers.
 */
export function NotificationBellPopover({
  ariaLabel,
  title,
  markAllReadLabel,
  emptyLabel,
  items,
  unreadCount = items.filter((item) => !item.read).length,
  onMarkAllRead,
  onItemClick,
  notice,
  triggerClassName,
}: NotificationBellPopoverProps) {
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<PanelPosition>({
    top: 0,
    left: 0,
    width: 320,
  });

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(320, Math.max(0, window.innerWidth - 16));
    const isRtl = document.documentElement.dir === "rtl";
    const candidateLeft = isRtl
      ? Math.min(rect.right, window.innerWidth - 8) - width
      : Math.max(8, rect.left);
    setPosition({
      top: rect.bottom + 8,
      left: Math.max(
        8,
        Math.min(candidateLeft, window.innerWidth - width - 8),
      ),
      width,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (
        !panelRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }

    window.addEventListener("resize", updatePosition);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, updatePosition]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={() => setIsOpen((open) => !open)}
        ariaLabel={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        aria-haspopup="dialog"
        data-ui="notification-bell"
        variant="ghost"
        size="sm"
        shape="round"
        className={`relative ${triggerClassName ?? "text-[color:var(--text-muted)]"}`}
        icon={
          <>
            <BellIcon />
            {unreadCount > 0 ? (
              <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[color:var(--error-500)] px-1 text-[10px] font-bold text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            ) : null}
          </>
        }
      />

      {isOpen
        ? createPortal(
            <div
              id={panelId}
              ref={panelRef}
              role="dialog"
              aria-label={title}
              data-ui="notification-popover"
              className="fixed z-[9999] overflow-hidden rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-lg"
              style={position}
            >
              <div className="flex items-center justify-between border-b border-[color:var(--border-light)] px-4 py-3">
                <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">
                  {title}
                </h3>
                {unreadCount > 0 ? (
                  <Button
                    variant="link"
                    type="button"
                    onClick={() => void onMarkAllRead()}
                    className="px-0 text-xs font-medium text-[color:var(--primary-500)] hover:bg-transparent hover:text-[color:var(--primary-600)]"
                  >
                    {markAllReadLabel}
                  </Button>
                ) : null}
              </div>

              {notice ? (
                <div
                  data-ui="notification-notice"
                  className="border-b border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-4 py-3"
                >
                  <p className="text-xs font-semibold text-[color:var(--text-primary)]">
                    {notice.title}
                  </p>
                  <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                    {notice.body}
                  </p>
                  {notice.actionLabel && notice.onAction ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      isLoading={notice.actionLoading}
                      disabled={notice.actionLoading}
                      onClick={() => void notice.onAction?.()}
                    >
                      {notice.actionLabel}
                    </Button>
                  ) : null}
                </div>
              ) : null}

              <div className="no-scrollbar max-h-80 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="px-4 py-8 text-center text-xs text-[color:var(--text-muted)]">
                    {emptyLabel}
                  </div>
                ) : (
                  items.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      data-ui="notification-row"
                      onClick={() => {
                        setIsOpen(false);
                        void onItemClick(item);
                      }}
                      className={`flex w-full cursor-pointer flex-col gap-1 border-b border-[color:var(--border-light)] px-4 py-3 text-start transition last:border-b-0 hover:bg-[color:var(--surface-alt)] ${
                        item.read ? "" : "bg-[color:var(--primary-50)]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span
                          dir="auto"
                          className="text-xs font-semibold text-[color:var(--text-primary)]"
                        >
                          {item.title}
                        </span>
                        {!item.read ? (
                          <span
                            aria-hidden="true"
                            className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[color:var(--primary-500)]"
                          />
                        ) : null}
                      </div>
                      <span
                        dir="auto"
                        className="text-xs text-[color:var(--text-muted)]"
                      >
                        {item.body}
                      </span>
                      {item.timeLabel ? (
                        <span className="text-[11px] text-[color:var(--text-muted)]">
                          {item.timeLabel}
                        </span>
                      ) : null}
                    </button>
                  ))
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
